/**
 * 내선 관리 화면(ExtensionManage)의 공유 데이터 — 내선·전화기·설치 장소·상담사 번호.
 *
 * 세 sub-tab 이 서로의 목록을 조인한다(내선 행의 전화기 라벨, 전화기 행의 내선·상담사,
 * 설치 장소의 전화기 수). 그래서 목록은 호스트가 한 번 받아 패널에 넘기고, 조작 뒤 재조회도
 * 여기로 모은다. admin 에는 콜봇 WS 가 없으므로 "가만히 있어도 바뀌는 값" 은 아래 조건부
 * 폴링이 GET 재조회로 따라잡는다.
 *
 * 미배포 판정은 sub-tab 별이다: 7단계 전용 라우트 GET pbx-sites 가 미구현이면 전화기·설치 장소는
 * 안내 한 줄, 내선은 4단계 GET pbx-extensions 만으로 그린다. pbx-extensions 까지 미구현이면
 * 내선도 안내 한 줄이다.
 *
 * 회사(병원) 스코프: studio 와 달리 admin 은 병원을 골라야 한다. `usePbxInventory(companyIdRef)`
 * 로 회사 id ref 를 받아, `api` 의 모든 메서드에 첫 인자로 끼워 넣는다(컴포넌트는 원본과 같이
 * `inventory.api.claimPbxDevice(id, payload)` 처럼 부른다). 회사가 바뀌면 네 목록과 4·7단계
 * 판정을 비우고 다시 받으며, 진행 중이던 요청의 응답은 순번 + 회사 키로 버린다.
 *
 * 조건부 폴링 규칙(이전 가이드 "WS 없이 옮기기 › 권고"):
 * - 도는 조건(하나라도 참): 회전 진행 중인 내선이 있다(`isRotationInProgress`) /
 *   `설정 적용 중` 칩이 뜬 내선이 있다(`provisioningChip`) / 전화기 sub-tab 이 열려 있다
 *   (호스트가 `setActiveSub('devices')` 로 알린다).
 * - 부르는 것: `reload(['extensions', 'devices'])`. 둘 다 GET 이고 순번 가드가 늦은 응답을
 *   버린다. `stage7 === false` 면 devices 는 기존 가드대로 부르지 않는다.
 *   `POST pbx-extensions/refresh`(`[지금 확인]`)는 PBX 호스트까지 조회하므로 폴링에 쓰지 않는다.
 * - 주기: `PBX_POLL_INTERVAL_MS`(출발값 10초). 실측 [26](전화기 설정 수신 주기)이 정해지면 맞춘다.
 * - 멈춤: 조건이 모두 거짓 / 브라우저 탭 숨김(`document.visibilityState === 'hidden'`,
 *   `visibilitychange` 로 재개) / 언마운트 / 회사 id 없음·변경 중. 조작이 진행 중이면
 *   (`setActionPending(true)`, usePbxAction 이 알린다) 그 틱은 건너뛴다. 앞 틱의 재조회가 아직
 *   끝나지 않았어도 건너뛴다.
 * - `polling` ref 로 현재 도는지 노출한다(헤더 `자동 갱신 중` 표시용).
 *
 * 계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 공통",
 *       ~/md/front/admin-콜봇-내선관리-화면-요청.md "요청 계약 2·4"
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';

import { isCallApiUnimplemented } from '@/views/Callbot/service/callApiError';
import { getPbxApi, isPbxMockEnabled } from '@/views/Callbot/service/pbxProvisioningMock';
import { isRotationInProgress, provisioningChip } from '@/views/Callbot/service/pbxExtensions';

/**
 * @typedef {'extensions' | 'devices' | 'sites' | 'counselorNumbers'} PbxResource
 */

/**
 * @typedef {Object} PbxResourceState
 * @property {import('vue').Ref<Array>} items
 * @property {import('vue').Ref<boolean>} loading
 * @property {import('vue').Ref<boolean>} error 실패(미구현 제외) — "없음" 과 구분해 다시 시도를 보인다
 * @property {import('vue').Ref<boolean>} loaded 한 번이라도 받아 왔는가 — 첫 로딩 스켈레톤 판정
 */

/** 조건부 폴링 주기. 실측 [26](전화기 설정 수신 주기)이 정해지면 그 값에 맞춘다 */
export const PBX_POLL_INTERVAL_MS = 10 * 1000;

const NOW_TICK_MS = 30 * 1000;

/**
 * 회사 id 를 첫 인자로 끼워 넣어 감쌀 메서드 — `callbotApi`·`pbxMockApi` 공통 시그니처
 * (이식 계약 §2). `searchHospitals` 는 inapi 호출이라 여기 없다.
 */
const PBX_API_METHODS = [
  'listCounselorNumbers',
  'listPbxExtensions',
  'refreshPbxExtensions',
  'releasePbxExtension',
  'syncCounselorExtension',
  'lockCounselorExtension',
  'unlockCounselorExtension',
  'rotateCounselorExtensionSecret',
  'disableCounselorExtension',
  'resumeCounselorExtension',
  'checkSyncCounselorExtension',
  'listPbxDevices',
  'createPbxDevice',
  'updatePbxDevice',
  'claimPbxDevice',
  'unassignPbxDevice',
  'deletePbxDevice',
  'rotatePbxDeviceAdminPassword',
  'getPbxDeviceAdminPassword',
  'updatePbxDeviceTransport',
  'checkSyncPbxDevice',
  'createPbxDeviceRecoveryCode',
  'listPbxDeviceLogs',
  'downloadPbxDeviceLog',
  'listPbxSites',
  'createPbxSite',
  'updatePbxSite',
  'deletePbxSite',
  'rotatePbxSiteToken',
  'revokePreviousPbxSiteToken',
  'getPbxSiteProvisioningUrl',
];

const NO_COMPANY_MESSAGE = '병원을 선택하세요.';

/** @returns {PbxResourceState} */
function resourceState() {
  return { items: ref([]), loading: ref(false), error: ref(false), loaded: ref(false) };
}

function byId(items) {
  return new Map(items.map((x) => [x.id, x]));
}

/**
 * 회사 id 가 바인딩된 api 객체 — `raw[name](companyId, ...args)`. 회사가 비어 있으면 reject.
 * 요청 시점의 id 를 읽으므로 병원을 바꾼 뒤의 조작은 새 병원으로 나간다.
 */
function bindCompanyApi(raw, companyId) {
  const bound = {};
  for (const name of PBX_API_METHODS) {
    if (typeof raw[name] !== 'function') continue;
    bound[name] = (...args) => {
      const id = companyId.value;
      if (!id) return Promise.reject(new Error(NO_COMPANY_MESSAGE));
      return raw[name](id, ...args);
    };
  }
  return bound;
}

/**
 * @param {import('vue').Ref<string | null | undefined>} companyIdRef 선택한 병원 id(`companies._id`)
 */
export function usePbxInventory(companyIdRef) {
  /** @type {import('vue').ComputedRef<string | null>} */
  const companyId = computed(() => {
    const v = companyIdRef.value;
    return v ? String(v) : null;
  });
  const api = bindCompanyApi(getPbxApi(), companyId);
  const mock = isPbxMockEnabled();

  const stage7 = ref(null);
  const stage4 = ref(null);
  const extensions = resourceState();
  const devices = resourceState();
  const sites = resourceState();
  const counselorNumbers = resourceState();
  const now = ref(Date.now());

  // 연속 재조회에서 늦게 온 앞 응답이 새 응답을 덮지 않게 — 자원별 마지막 요청만 반영.
  // 회사가 바뀌면 resetAll 이 전부 올리고, load 안에서도 회사 키를 한 번 더 대조한다.
  /** @type {Record<PbxResource, number>} */
  const seq = { extensions: 0, devices: 0, sites: 0, counselorNumbers: 0 };

  /**
   * @param {PbxResource} key
   * @param {PbxResourceState} state
   * @param {() => Promise<{ items?: Array }>} fetcher
   * @param {(() => void)=} onUnimplemented
   * @returns {Promise<boolean | null>} true 반영 / false 실패·미구현 / null 버린 응답(순번·회사 불일치)
   *   — 버린 응답은 판정(stage4·stage7)에 쓰면 안 되므로 true 와 구분한다
   */
  async function load(key, state, fetcher, onUnimplemented) {
    const my = ++seq[key];
    const company = companyId.value;
    const stale = () => my !== seq[key] || company !== companyId.value;
    state.loading.value = true;
    state.error.value = false;
    try {
      const res = await fetcher();
      if (stale()) return null;
      state.items.value = res?.items ?? [];
      state.loaded.value = true;
      return true;
    } catch (err) {
      if (stale()) return null;
      if (onUnimplemented && isCallApiUnimplemented(err)) {
        onUnimplemented();
        state.items.value = [];
      } else {
        state.error.value = true;
      }
      return false;
    } finally {
      if (!stale()) state.loading.value = false;
    }
  }

  const loadExtensions = () =>
    load('extensions', extensions, () => api.listPbxExtensions(), () => {
      stage4.value = false;
    }).then((ok) => {
      // null(버린 응답)은 이전 회사·이전 요청의 결과 — 새 회사의 판정을 덮지 않는다
      if (ok === true) stage4.value = true;
    });

  const loadCounselorNumbers = () =>
    load('counselorNumbers', counselorNumbers, () => api.listCounselorNumbers());

  // 설치 장소 목록이 7단계 판정 프로브를 겸한다 — 미구현이면 전화기는 부르지 않는다
  const loadSites = () =>
    load('sites', sites, () => api.listPbxSites(), () => {
      stage7.value = false;
    }).then((ok) => {
      // null(버린 응답)은 이전 회사·이전 요청의 결과 — 새 회사의 판정을 덮지 않는다
      if (ok === true) stage7.value = true;
    });

  const loadDevices = () =>
    stage7.value ? load('devices', devices, () => api.listPbxDevices()).then(() => undefined) : Promise.resolve();

  async function loadAll() {
    const company = companyId.value;
    if (!company) return;
    now.value = Date.now();
    await Promise.all([loadSites(), loadExtensions(), loadCounselorNumbers()]);
    // 기다리는 사이 회사가 바뀌었으면 이전 회사의 흐름을 새 회사로 잇지 않는다(새 회사는 watch 가 따로 부른다)
    if (company !== companyId.value) return;
    await loadDevices();
  }

  /** @param {PbxResource[]=} which */
  async function reload(which = ['extensions', 'devices', 'sites', 'counselorNumbers']) {
    if (!companyId.value) return;
    now.value = Date.now();
    const jobs = [];
    if (which.includes('extensions')) jobs.push(loadExtensions());
    if (which.includes('counselorNumbers')) jobs.push(loadCounselorNumbers());
    if (which.includes('sites')) jobs.push(loadSites());
    if (which.includes('devices')) jobs.push(loadDevices());
    await Promise.all(jobs);
  }

  // 전화기는 7단계 판정 뒤에만 부른다 — 프로브가 이번에 성공했으면 이어서 받아야 한다
  async function reprobe() {
    const company = companyId.value;
    if (!company) return;
    await reload(['sites']);
    if (company !== companyId.value) return;
    if (stage7.value) await reload(['devices']);
  }

  function replaceExtensions(items, since) {
    // [지금 확인] 이 도는 사이 조작 뒤 재조회가 시작됐으면, 아래 순번 증가가 그 새 결과를 버린다 — 다시 받는다
    const superseded = since !== undefined && since !== seq.extensions;
    // 진행 중인 목록 요청이 뒤늦게 덮지 않게 순번을 올린다
    seq.extensions += 1;
    extensions.items.value = items;
    extensions.loading.value = false;
    extensions.error.value = false;
    extensions.loaded.value = true;
    now.value = Date.now();
    if (superseded) void loadExtensions();
  }

  // ---------------------------------------------------------------------------
  // 30초 `now` 틱 — `설정 적용 중` 파생 칩과 상대 시각을 다시 계산하는 시계
  // ---------------------------------------------------------------------------
  let tick = null;
  function startTick() {
    if (tick) return;
    tick = setInterval(() => {
      now.value = Date.now();
    }, NOW_TICK_MS);
  }
  function stopTick() {
    if (tick) clearInterval(tick);
    tick = null;
  }
  startTick();

  // ---------------------------------------------------------------------------
  // 조건부 폴링 — 규칙은 파일 머리 주석
  // ---------------------------------------------------------------------------
  const deviceById = computed(() => byId(devices.items.value));

  /** 호스트가 알려 주는 현재 sub-tab('extensions' | 'devices' | 'sites') */
  const activeSub = ref(null);
  function setActiveSub(sub) {
    activeSub.value = sub ?? null;
  }

  /** usePbxAction 이 조작 중임을 알린다 — 그 틱은 건너뛴다 */
  const actionPending = ref(false);
  function setActionPending(on) {
    actionPending.value = !!on;
  }

  const hasDocument = typeof document !== 'undefined';
  const visible = ref(!hasDocument || document.visibilityState !== 'hidden');
  function onVisibilityChange() {
    visible.value = document.visibilityState !== 'hidden';
  }
  if (hasDocument) document.addEventListener('visibilitychange', onVisibilityChange);

  const needsPolling = computed(() => {
    if (activeSub.value === 'devices') return true;
    const items = extensions.items.value;
    if (items.some((x) => isRotationInProgress(x.rotation))) return true;
    const map = deviceById.value;
    const at = now.value;
    return items.some((x) => !!provisioningChip(x, x.device_id ? map.get(x.device_id) : null, at));
  });

  const shouldPoll = computed(() => !!companyId.value && visible.value && needsPolling.value);

  const polling = ref(false);
  let pollTimer = null;
  let pollInFlight = false;

  async function pollTick() {
    if (!companyId.value || actionPending.value || pollInFlight) return;
    pollInFlight = true;
    try {
      await reload(['extensions', 'devices']);
    } finally {
      pollInFlight = false;
    }
  }
  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      void pollTick();
    }, PBX_POLL_INTERVAL_MS);
    polling.value = true;
  }
  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
    polling.value = false;
  }
  function syncPolling() {
    if (shouldPoll.value) startPolling();
    else stopPolling();
  }
  watch(shouldPoll, syncPolling, { immediate: true });

  // ---------------------------------------------------------------------------
  // 회사 변경 — 네 목록·판정을 비우고, 진행 중 요청은 순번으로 버리고, 새 회사를 받는다.
  // 패널이 열어 둔 상세·모달·1회 표시 값은 호스트가 `:key="companyId"` 로 다시 마운트해 지운다.
  // ---------------------------------------------------------------------------
  function resetResource(state) {
    state.items.value = [];
    state.loading.value = false;
    state.error.value = false;
    state.loaded.value = false;
  }
  function resetAll() {
    for (const key of Object.keys(seq)) seq[key] += 1;
    stopPolling();
    resetResource(extensions);
    resetResource(devices);
    resetResource(sites);
    resetResource(counselorNumbers);
    stage4.value = null;
    stage7.value = null;
    now.value = Date.now();
  }
  // immediate 아님 — 첫 로드는 호스트의 onMounted 가 loadAll() 로 한 번만 부른다
  watch(companyId, (id, prev) => {
    if (id === prev) return;
    resetAll();
    if (id) void loadAll();
    syncPolling();
  });

  onBeforeUnmount(() => {
    stopTick();
    stopPolling();
    if (hasDocument) document.removeEventListener('visibilitychange', onVisibilityChange);
  });

  return {
    api,
    mock,
    /** 선택한 병원 id. null 이면 미선택 */
    companyId,
    stage7,
    // 미구현이면 stage7 이 false 로 판정된다 — null 인 채 실패면 판정 자체를 못 한 것
    stage7ProbeFailed: computed(() => stage7.value === null && sites.error.value),
    stage4,
    extensions,
    devices,
    sites,
    counselorNumbers,
    now,
    // admin 은 CERTILIFE 뿐이고 강제는 call-api 가 한다 — 역할 판정 없이 항상 보인다(호출부 호환)
    canRelease: computed(() => true),
    extensionById: computed(() => byId(extensions.items.value)),
    deviceById,
    siteById: computed(() => byId(sites.items.value)),
    counselorNumberById: computed(() => byId(counselorNumbers.items.value)),
    loadAll,
    reload,
    reprobe,
    extensionsSeq: () => seq.extensions,
    replaceExtensions,
    /** 조건부 폴링이 도는 중 — 헤더 표시용 */
    polling,
    setActiveSub,
    setActionPending,
  };
}
